import { useState, useMemo } from 'react';
import {
  Sparkles,
  Code2,
  User,
  List,
  Quote,
  HelpCircle,
  Calendar,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Info,
  ExternalLink,
  CheckCircle,
  Loader,
  Lightbulb,
} from 'lucide-react';
import type { PageResult, GeoCategoryScore, GeoIssue, GeoCategory, GeoIssueSeverity } from '../../types';

interface GeoTabProps {
  pageResults: PageResult[];
  loading?: boolean;
}

// Category score plus a count of advisory (recommendation) issues, which are
// kept separate from the scored severity buckets.
type AggCategoryScore = GeoCategoryScore & { advisoryCount: number };

// Severity filter, extended with an 'advisory' bucket for recommendation-only items.
type GeoFilter = GeoIssueSeverity | 'advisory' | 'all';

// Category configuration with icons and descriptions
const CATEGORY_CONFIG: Record<GeoCategory, { label: string; description: string; icon: React.ElementType }> = {
  schema_markup: {
    label: 'Schema Markup',
    description: 'FAQ, HowTo, Q&A, and Article schemas',
    icon: Code2,
  },
  eeat_signals: {
    label: 'E-E-A-T Signals',
    description: 'Experience, Expertise, Authority, Trust',
    icon: User,
  },
  content_structure: {
    label: 'Content Structure',
    description: 'Lists, tables, headings, definitions',
    icon: List,
  },
  citation_quality: {
    label: 'Citation Quality',
    description: 'Statistics, sources, references',
    icon: Quote,
  },
  answer_optimization: {
    label: 'Answer Optimization',
    description: 'Question patterns, direct answers',
    icon: HelpCircle,
  },
  freshness_signals: {
    label: 'Freshness Signals',
    description: 'Publication dates, last modified',
    icon: Calendar,
  },
};

function getScoreColor(score: number): string {
  if (score >= 90) return 'text-status-success';
  if (score >= 70) return 'text-status-warning';
  if (score >= 50) return 'text-orange-500';
  return 'text-status-error';
}

function getScoreBgColor(score: number): string {
  if (score >= 90) return 'bg-status-success';
  if (score >= 70) return 'bg-status-warning';
  if (score >= 50) return 'bg-orange-500';
  return 'bg-status-error';
}

function getSeverityIcon(severity: GeoIssueSeverity) {
  switch (severity) {
    case 'critical':
      return <AlertCircle size={16} className="text-status-error" />;
    case 'warning':
      return <AlertTriangle size={16} className="text-status-warning" />;
    case 'info':
      return <Info size={16} className="text-blue-500" />;
  }
}

function getSeverityBadgeClasses(severity: GeoIssueSeverity): string {
  switch (severity) {
    case 'critical':
      return 'bg-status-error/10 text-status-error';
    case 'warning':
      return 'bg-status-warning/10 text-status-warning';
    case 'info':
      return 'bg-blue-500/10 text-blue-500';
  }
}

// Circular gauge component
function CircularGauge({ score, size = 140 }: { score: number; size?: number }) {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-light"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-all duration-700 ${score >= 90 ? 'stroke-status-success' : score >= 70 ? 'stroke-status-warning' : score >= 50 ? 'stroke-orange-500' : 'stroke-status-error'}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-display font-bold ${getScoreColor(score)}`}>
          {score}
        </span>
        <span className="text-caption text-gray-medium">/ 100</span>
      </div>
    </div>
  );
}

// Category card component
function CategoryCard({
  categoryScore,
  isExpanded,
  onToggle,
  issues,
}: {
  categoryScore: AggCategoryScore;
  isExpanded: boolean;
  onToggle: () => void;
  issues: GeoIssue[];
}) {
  const config = CATEGORY_CONFIG[categoryScore.category];
  const Icon = config.icon;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-card ${categoryScore.score >= 90 ? 'bg-status-success/10' : categoryScore.score >= 70 ? 'bg-status-warning/10' : 'bg-status-error/10'}`}>
            <Icon size={24} className={getScoreColor(categoryScore.score)} />
          </div>
          <div>
            <h4 className="text-body font-semibold">{config.label}</h4>
            <p className="text-caption text-gray-medium">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {/* Issue counts */}
          <div className="flex items-center gap-3 text-body-sm">
            {categoryScore.criticalCount > 0 && (
              <span className="flex items-center gap-1 text-status-error">
                <AlertCircle size={14} />
                {categoryScore.criticalCount}
              </span>
            )}
            {categoryScore.warningCount > 0 && (
              <span className="flex items-center gap-1 text-status-warning">
                <AlertTriangle size={14} />
                {categoryScore.warningCount}
              </span>
            )}
            {categoryScore.infoCount > 0 && (
              <span className="flex items-center gap-1 text-blue-500">
                <Info size={14} />
                {categoryScore.infoCount}
              </span>
            )}
            {categoryScore.advisoryCount > 0 && (
              <span className="flex items-center gap-1 text-purple-500" title="Advisory recommendations">
                <Lightbulb size={14} />
                {categoryScore.advisoryCount}
              </span>
            )}
          </div>
          {/* Score */}
          <div className="flex items-center gap-3">
            <div className="w-16">
              <div className="h-2 bg-gray-light rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${getScoreBgColor(categoryScore.score)}`}
                  style={{ width: `${categoryScore.score}%` }}
                />
              </div>
            </div>
            <span className={`text-body font-semibold w-10 text-right ${getScoreColor(categoryScore.score)}`}>
              {categoryScore.score}
            </span>
          </div>
          {isExpanded ? (
            <ChevronDown size={20} className="text-gray-medium" />
          ) : (
            <ChevronRight size={20} className="text-gray-medium" />
          )}
        </div>
      </button>

      {/* Expanded issues list */}
      {isExpanded && issues.length > 0 && (
        <div className="border-t border-gray-light">
          {issues.map((issue) => (
            <IssueItem key={issue.id} issue={issue} />
          ))}
        </div>
      )}
      {isExpanded && issues.length === 0 && (
        <div className="border-t border-gray-light p-4 text-center text-gray-medium">
          No issues found in this category
        </div>
      )}
    </div>
  );
}

// Individual issue item
function IssueItem({ issue }: { issue: GeoIssue }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b border-gray-light last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-3 p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        {issue.advisory ? (
          <Lightbulb size={16} className="text-purple-500" />
        ) : (
          getSeverityIcon(issue.severity)
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-body font-medium text-dark-text">
              {issue.title}
            </span>
            {issue.advisory ? (
              <span className="px-2 py-0.5 rounded text-caption font-medium bg-purple-500/10 text-purple-600">
                Advisory
              </span>
            ) : (
              <span className={`px-2 py-0.5 rounded text-caption font-medium ${getSeverityBadgeClasses(issue.severity)}`}>
                {issue.severity}
              </span>
            )}
          </div>
          {!isExpanded && (
            <p className="text-body-sm text-gray-medium mt-1 line-clamp-1">
              {issue.description}
            </p>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown size={16} className="text-gray-medium flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight size={16} className="text-gray-medium flex-shrink-0 mt-1" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pl-10 space-y-3">
          {issue.pageUrl && (
            <div className="flex items-center gap-2">
              <ExternalLink size={14} className="text-gray-medium flex-shrink-0" />
              <a
                href={issue.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:text-coral/80 truncate"
                title={issue.pageUrl}
              >
                {issue.pageUrl}
              </a>
            </div>
          )}

          <p className="text-body-sm text-gray-medium">
            {issue.description}
          </p>

          <div className="p-3 bg-purple-500/5 rounded-card border border-purple-500/20">
            <p className="text-body-sm font-medium text-purple-600 mb-1">AI Discoverability Impact</p>
            <p className="text-body-sm text-gray-medium">{issue.impact}</p>
          </div>

          <div className="p-3 bg-status-success/5 rounded-card border border-status-success/20">
            <p className="text-body-sm font-medium text-status-success mb-1">Recommendation</p>
            <p className="text-body-sm text-gray-medium">{issue.recommendation}</p>
          </div>

          {issue.element && (
            <div className="p-3 bg-gray-light/50 rounded-card">
              <p className="text-caption font-medium text-gray-medium mb-1">DOM Element</p>
              <code className="text-caption text-dark-text break-all">
                {issue.element}
              </code>
            </div>
          )}

          {issue.referenceUrl && (
            <a
              href={issue.referenceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-body-sm text-coral hover:text-coral/80"
            >
              Learn More
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// Detected schemas display
function DetectedSchemas({ schemas }: { schemas: string[] }) {
  if (schemas.length === 0) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-gray-medium">
        <AlertTriangle size={16} className="text-status-warning" />
        No JSON-LD schemas detected
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {schemas.map((schema) => (
        <span
          key={schema}
          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-600 rounded text-body-sm font-medium"
        >
          <CheckCircle size={14} />
          {schema}
        </span>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card text-center py-12">
      <Sparkles size={48} className="mx-auto text-gray-medium opacity-50 mb-4" />
      <h3 className="text-heading-3 font-semibold mb-2">No GEO Data Available</h3>
      <p className="text-body text-gray-medium max-w-md mx-auto">
        GEO (Generative Engine Optimization) analysis was not performed during this scan. Run a new scan with GEO enabled to see AI search engine optimization recommendations.
      </p>
    </div>
  );
}

export function GeoTab({ pageResults, loading }: GeoTabProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<GeoCategory>>(new Set());
  const [filterSeverity, setFilterSeverity] = useState<GeoFilter>('all');

  // Aggregate GEO results from all pages
  const aggregatedData = useMemo(() => {
    const pagesWithGeo = pageResults.filter((p) => p.geoResult);

    if (pagesWithGeo.length === 0) {
      return null;
    }

    // Aggregate scores across all pages
    const categoryTotals: Record<GeoCategory, { totalScore: number; count: number; issues: GeoIssue[] }> = {
      schema_markup: { totalScore: 0, count: 0, issues: [] },
      eeat_signals: { totalScore: 0, count: 0, issues: [] },
      content_structure: { totalScore: 0, count: 0, issues: [] },
      citation_quality: { totalScore: 0, count: 0, issues: [] },
      answer_optimization: { totalScore: 0, count: 0, issues: [] },
      freshness_signals: { totalScore: 0, count: 0, issues: [] },
    };

    let totalOverallScore = 0;
    const allIssues: GeoIssue[] = [];
    const allSchemas = new Set<string>();

    for (const page of pagesWithGeo) {
      const geoResult = page.geoResult!;
      totalOverallScore += geoResult.overallScore;

      // Collect detected schemas
      for (const schema of geoResult.detectedSchemas) {
        allSchemas.add(schema);
      }

      // Add issues with page URL context
      for (const issue of geoResult.issues) {
        const issueWithPage = {
          ...issue,
          id: `${page.pageUrl}-${issue.id}`,
          pageUrl: page.pageUrl,
        };
        allIssues.push(issueWithPage);
        categoryTotals[issue.category].issues.push(issueWithPage);
      }

      // Aggregate category scores
      for (const catScore of geoResult.categoryScores) {
        categoryTotals[catScore.category].totalScore += catScore.score;
        categoryTotals[catScore.category].count++;
      }
    }

    // Calculate average scores. Advisory (recommendation) issues are counted
    // separately from the scored severity buckets.
    const categoryScores: AggCategoryScore[] = (Object.keys(categoryTotals) as GeoCategory[])
      .filter((cat) => categoryTotals[cat].count > 0)
      .map((category) => {
        const data = categoryTotals[category];
        const avgScore = Math.round(data.totalScore / data.count);
        const issues = data.issues;
        const scored = issues.filter((i) => !i.advisory);

        return {
          category,
          score: avgScore,
          issueCount: issues.length,
          criticalCount: scored.filter((i) => i.severity === 'critical').length,
          warningCount: scored.filter((i) => i.severity === 'warning').length,
          infoCount: scored.filter((i) => i.severity === 'info').length,
          advisoryCount: issues.filter((i) => i.advisory).length,
        };
      })
      .sort((a, b) => a.score - b.score); // Sort by score ascending (worst first)

    const overallScore = Math.round(totalOverallScore / pagesWithGeo.length);

    return {
      overallScore,
      categoryScores,
      allIssues,
      categoryIssues: categoryTotals,
      pagesAnalyzed: pagesWithGeo.length,
      detectedSchemas: Array.from(allSchemas),
    };
  }, [pageResults]);

  if (!aggregatedData) {
    if (loading) {
      return (
        <div className="card text-center py-12">
          <Loader size={48} className="mx-auto text-coral animate-spin mb-4" />
          <p className="text-body text-gray-medium">Loading GEO data…</p>
        </div>
      );
    }
    return <EmptyState />;
  }

  const toggleCategory = (category: GeoCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Filter issues
  const getFilteredIssues = (category: GeoCategory): GeoIssue[] => {
    const issues = aggregatedData.categoryIssues[category].issues;
    if (filterSeverity === 'all') return issues;
    if (filterSeverity === 'advisory') return issues.filter((i) => i.advisory);
    return issues.filter((i) => i.severity === filterSeverity && !i.advisory);
  };

  // Summary stats — scored severity buckets exclude advisory recommendations,
  // which are tallied on their own.
  const scoredIssues = aggregatedData.allIssues.filter((i) => !i.advisory);
  const totalCritical = scoredIssues.filter((i) => i.severity === 'critical').length;
  const totalWarnings = scoredIssues.filter((i) => i.severity === 'warning').length;
  const totalInfo = scoredIssues.filter((i) => i.severity === 'info').length;
  const totalAdvisory = aggregatedData.allIssues.filter((i) => i.advisory).length;

  return (
    <div className="space-y-8">
      {/* Overview Section */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <Sparkles className="text-purple-500" size={24} />
          GEO Analysis
          <span className="text-body-sm text-gray-medium font-normal">(Generative Engine Optimization)</span>
        </h3>

        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Score Gauge */}
          <div className="flex flex-col items-center">
            <CircularGauge score={aggregatedData.overallScore} />
            <p className="text-body-sm text-gray-medium mt-3">AI Discoverability Score</p>
            <p className="text-caption text-gray-medium">
              {aggregatedData.pagesAnalyzed} page{aggregatedData.pagesAnalyzed !== 1 ? 's' : ''} analyzed
            </p>
          </div>

          {/* Summary Stats */}
          <div className="flex-1 w-full">
            <h4 className="text-body font-semibold mb-3 text-gray-medium">Issue Summary</h4>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'critical' ? 'all' : 'critical')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'critical'
                    ? 'bg-status-error/10 border-status-error'
                    : 'bg-status-error/5 border-status-error/20 hover:border-status-error/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle size={18} className="text-status-error" />
                  <span className="text-heading-2 font-bold text-status-error">
                    {totalCritical}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Critical</span>
              </button>
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'warning' ? 'all' : 'warning')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'warning'
                    ? 'bg-status-warning/10 border-status-warning'
                    : 'bg-status-warning/5 border-status-warning/20 hover:border-status-warning/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={18} className="text-status-warning" />
                  <span className="text-heading-2 font-bold text-status-warning">
                    {totalWarnings}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Warnings</span>
              </button>
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'info' ? 'all' : 'info')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'info'
                    ? 'bg-blue-500/10 border-blue-500'
                    : 'bg-blue-500/5 border-blue-500/20 hover:border-blue-500/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Info size={18} className="text-blue-500" />
                  <span className="text-heading-2 font-bold text-blue-500">
                    {totalInfo}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Info</span>
              </button>
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'advisory' ? 'all' : 'advisory')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'advisory'
                    ? 'bg-purple-500/10 border-purple-500'
                    : 'bg-purple-500/5 border-purple-500/20 hover:border-purple-500/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb size={18} className="text-purple-500" />
                  <span className="text-heading-2 font-bold text-purple-500">
                    {totalAdvisory}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Advisory</span>
              </button>
            </div>
            {filterSeverity !== 'all' && (
              <button
                onClick={() => setFilterSeverity('all')}
                className="mt-3 text-body-sm text-coral hover:underline"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Detected Schemas Section */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
          <Code2 className="text-purple-500" size={24} />
          Detected JSON-LD Schemas
        </h3>
        <DetectedSchemas schemas={aggregatedData.detectedSchemas} />
      </div>

      {/* Categories Section */}
      <div>
        <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
          <List className="text-purple-500" size={24} />
          GEO Categories
        </h3>
        <div className="space-y-4">
          {aggregatedData.categoryScores.map((catScore) => (
            <CategoryCard
              key={catScore.category}
              categoryScore={catScore}
              isExpanded={expandedCategories.has(catScore.category)}
              onToggle={() => toggleCategory(catScore.category)}
              issues={getFilteredIssues(catScore.category)}
            />
          ))}
        </div>
      </div>

      {/* About GEO */}
      <div className="card bg-purple-500/5 border border-purple-500/20">
        <h4 className="text-body font-semibold text-purple-600 mb-2 flex items-center gap-2">
          <Sparkles size={18} />
          What is GEO?
        </h4>
        <p className="text-body-sm text-gray-medium">
          Generative Engine Optimization (GEO) helps your content get discovered and cited by AI-powered search engines like Google SGE, Bing Chat, Perplexity, and ChatGPT. By optimizing your content structure, adding relevant schemas, and improving E-E-A-T signals, you increase the chances of your content being featured in AI-generated responses.
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-body-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-success" />
          <span className="text-gray-medium">Excellent (90-100)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-warning" />
          <span className="text-gray-medium">Good (70-89)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="text-gray-medium">Needs Work (50-69)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-error" />
          <span className="text-gray-medium">Poor (0-49)</span>
        </div>
      </div>
    </div>
  );
}
